/**
 * 마커 겹침 감지 및 클러스터 병합
 *
 * 피그마 기준: "사용자가 보는 시점에서 마커 외곽 영역이 조금이라도 겹칠경우 클러스터 마커로 표시"
 *
 * 모든 마커 유형(서버 클러스터, 서버 그룹, 단일 매물) 간 겹침을 판정하고,
 * 겹치는 마커들을 하나의 클러스터로 병합한다.
 *
 * 병합된 클러스터에는 `_merged` 배열이 포함되어,
 * 클릭 시 서버에 전달하여 OR 조건으로 정확한 매물 조회가 가능하다.
 */
import legacy from "../../../utils/legacy";

// 마커 크기 추정 (피그마 기준)
const SINGLE_WIDTH = 130;
const SINGLE_HEIGHT = 67;   // 카드(59) + 꼬리(8)

// 클러스터 크기 (count별)
function getClusterSize(count) {
  if (count < 10) return 36;
  if (count < 100) return 44;
  return 54;
}

/**
 * 마커의 바운딩 박스 계산
 * 앵커: bottom-center (xAnchor: 0.5, yAnchor: 1)
 */
function getBBox(screenPos, marker) {
  const isSingle = marker.type !== "cluster" && (marker.count || 1) === 1;

  if (isSingle) {
    return {
      left: screenPos.x - SINGLE_WIDTH / 2,
      right: screenPos.x + SINGLE_WIDTH / 2,
      top: screenPos.y - SINGLE_HEIGHT,
      bottom: screenPos.y,
    };
  }

  // 클러스터는 원형 — center-center 앵커로 취급
  const size = getClusterSize(marker.count || 2);
  const half = size / 2;
  return {
    left: screenPos.x - half,
    right: screenPos.x + half,
    top: screenPos.y - half,
    bottom: screenPos.y + half,
  };
}

function boxesOverlap(a, b) {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * Union-Find
 */
function createUnionFind(n) {
  const parent = Array.from({ length: n }, (_, i) => i);
  const rank = new Array(n).fill(0);

  function find(x) {
    if (parent[x] !== x) parent[x] = find(parent[x]);
    return parent[x];
  }

  function union(a, b) {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return;
    if (rank[ra] < rank[rb]) parent[ra] = rb;
    else if (rank[ra] > rank[rb]) parent[rb] = ra;
    else { parent[rb] = ra; rank[ra]++; }
  }

  return { find, union };
}

/**
 * 마커의 원본 조회 조건을 추출
 * 클릭 시 서버에 전달할 데이터
 */
function extractMergeInfo(marker) {
  if (marker.type === "cluster" && marker.sw_lat != null) {
    return {
      type: "bounds",
      sw_lat: marker.sw_lat,
      sw_lng: marker.sw_lng,
      ne_lat: marker.ne_lat,
      ne_lng: marker.ne_lng,
    };
  }
  return {
    type: "group",
    group_key: marker.u,
  };
}

/**
 * 겹치는 마커들을 클러스터로 병합
 *
 * @param {Array} markers — API에서 반환된 마커 데이터 배열
 * @returns {Array} — 병합된 마커 데이터 배열
 */
export function mergeOverlappingMarkers(markers) {
  if (!markers || markers.length <= 1) return markers;

  try {
    return _mergeOverlapping(markers);
  } catch (err) {
    console.warn("마커 겹침 판정 실패, 원본 사용:", err);
    return markers;
  }
}

function _mergeOverlapping(markers) {
  if (!legacy.map || !legacy.daum?.maps) return markers;

  const projection = legacy.map.getProjection?.();
  if (!projection) return markers;

  // 화면 좌표 계산
  const screenPositions = markers.map((m) => {
    try {
      const latlng = new legacy.daum.maps.LatLng(m.lat, m.lng);
      const point = projection.pointFromCoords(latlng);
      return { x: point.x, y: point.y };
    } catch {
      return null;
    }
  });

  // 좌표 계산 실패한 마커는 제외
  const validIndices = [];
  const invalidMarkers = [];
  markers.forEach((m, i) => {
    if (screenPositions[i]) validIndices.push(i);
    else invalidMarkers.push(m);
  });

  if (validIndices.length <= 1) return markers;

  // 바운딩 박스 계산
  const bboxes = validIndices.map((i) => getBBox(screenPositions[i], markers[i]));

  // Union-Find로 겹치는 마커 그룹핑
  const uf = createUnionFind(validIndices.length);

  for (let i = 0; i < validIndices.length; i++) {
    for (let j = i + 1; j < validIndices.length; j++) {
      if (boxesOverlap(bboxes[i], bboxes[j])) {
        uf.union(i, j);
      }
    }
  }

  // 그룹별로 모으기
  const groups = {};
  for (let i = 0; i < validIndices.length; i++) {
    const root = uf.find(i);
    if (!groups[root]) groups[root] = [];
    groups[root].push(validIndices[i]);
  }

  // 결과 조립
  const result = [...invalidMarkers];
  let mergedCount = 0;

  for (const indices of Object.values(groups)) {
    if (indices.length === 1) {
      // 겹치지 않음 → 원본 그대로
      result.push(markers[indices[0]]);
    } else {
      // 겹치는 마커들 → 하나의 클러스터로 병합
      mergedCount++;
      const totalCount = indices.reduce((sum, idx) => sum + (markers[idx].count || 1), 0);
      const centerLat = indices.reduce((sum, idx) => sum + parseFloat(markers[idx].lat), 0) / indices.length;
      const centerLng = indices.reduce((sum, idx) => sum + parseFloat(markers[idx].lng), 0) / indices.length;

      // 각 원본 마커의 조회 조건을 보관
      const merged = indices.map((idx) => extractMergeInfo(markers[idx]));

      const mergedMarker = {
        type: "cluster",
        u: `merged_${indices.map((i) => markers[i].u).join("_")}`,
        count: totalCount,
        lat: centerLat,
        lng: centerLng,
        map_lv: legacy.map.getLevel(),
        _merged: merged,
      };

      result.push(mergedMarker);
    }
  }

  return result;
}
